import React, { useState } from "react";
import { Sparkles, Plus, X, Loader2, Rocket, Flag, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import api from "../lib/apiClient";

const PHASES = [
  { key: "phase1", title: "Days 1–30", sub: "Setup & Launch", icon: Rocket, color: "text-indigo-400", bar: "bg-indigo-500" },
  { key: "phase2", title: "Days 31–60", sub: "Traction & Sales", icon: Flag, color: "text-sky-400", bar: "bg-sky-500" },
  { key: "phase3", title: "Days 61–90", sub: "Scale & Optimize", icon: TrendingUp, color: "text-emerald-400", bar: "bg-emerald-500" },
];

const PRIO = {
  high: "bg-rose-500/15 text-rose-300",
  medium: "bg-amber-500/15 text-amber-300",
  low: "bg-slate-600/40 text-slate-300",
};

function Phase({ p, tasks, onChange, readOnly }) {
  const [text, setText] = useState("");
  const done = tasks.filter((t) => t.done).length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  const add = () => {
    if (!text.trim()) return;
    onChange([...tasks, { id: `t_${Date.now()}`, text: text.trim(), priority: "medium", done: false }]);
    setText("");
  };
  const toggle = (id) => onChange(tasks.map((t) => t.id === id ? { ...t, done: !t.done } : t));
  const remove = (id) => onChange(tasks.filter((t) => t.id !== id));

  return (
    <div data-testid={`action-phase-${p.key}`} className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 flex flex-col">
      <div className="flex items-center gap-2">
        <p.icon className={`h-5 w-5 ${p.color}`} />
        <div>
          <h3 className="font-display font-bold">{p.title}</h3>
          <p className="text-xs text-slate-400">{p.sub}</p>
        </div>
        <span className="ml-auto text-sm text-slate-400">{done}/{tasks.length}</span>
      </div>
      <Progress value={pct} className="h-1.5 bg-slate-800 mt-3" />

      <div className="space-y-2 mt-4 flex-1">
        {tasks.length === 0 && <p className="text-sm text-slate-500 italic">No tasks yet.</p>}
        {tasks.map((t) => (
          <div key={t.id} data-testid={`task-${t.id}`} className="group flex items-center gap-2 rounded-lg bg-slate-950/50 border border-slate-800 px-3 py-2">
            <Checkbox data-testid={`task-check-${t.id}`} checked={t.done} disabled={readOnly} onCheckedChange={() => toggle(t.id)} className="border-slate-600" />
            <span className={`text-sm flex-1 ${t.done ? "line-through text-slate-500" : "text-slate-200"}`}>{t.text}</span>
            <Badge className={`${PRIO[t.priority]} text-[10px] uppercase`}>{t.priority}</Badge>
            {!readOnly && (
              <button data-testid={`task-remove-${t.id}`} onClick={() => remove(t.id)} className="text-slate-500 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="flex gap-2 mt-4">
          <Input data-testid={`task-input-${p.key}`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add task…" className="bg-slate-900/70 border-slate-700 text-sm" />
          <Button data-testid={`task-add-${p.key}`} size="icon" onClick={add} className="bg-slate-700 hover:bg-slate-600 shrink-0"><Plus className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}

export default function ActionPlan({ plan, value, onChange, readOnly }) {
  const [loading, setLoading] = useState(false);

  const setPhase = (key, tasks) => onChange({ ...value, [key]: tasks });

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/ai/suggest", {
        type: "action_plan", business_name: plan.business_name, industry: plan.industry, description: plan.description,
      });
      onChange({
        phase1: [...(value.phase1 || []), ...(res.data.phase1 || [])],
        phase2: [...(value.phase2 || []), ...(res.data.phase2 || [])],
        phase3: [...(value.phase3 || []), ...(res.data.phase3 || [])],
      });
      toast.success("AI 90-day roadmap generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {!readOnly && (
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-display text-lg font-bold">90-Day Action Plan</h3>
          <Button data-testid="action-generate-button" onClick={generate} disabled={loading} className="cta-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />} Generate roadmap
          </Button>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-5">
        {PHASES.map((p) => (
          <Phase key={p.key} p={p} tasks={value[p.key] || []} onChange={(tasks) => setPhase(p.key, tasks)} readOnly={readOnly} />
        ))}
      </div>
    </div>
  );
}
